/* eslint-disable max-lines */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { cn } from 'lib/helpers/utility';
import { useGetWizardDelegatedTasks } from 'lib/hooks/queries/wizard/useGetWizardDelegatedTasks';

import state from 'components/state';

import DelegationTasksIntro from '../OnboardingWizard/components/DelegationTasksIntro/DelegationTasksIntro';
import IntroStep from '../OnboardingWizard/components/IntroStep/IntroStep';
import componentSteps, { StepPlaceholder } from '../OnboardingWizard/lib/componentSteps';

import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import StepSidebar from './components/Sidebar/Sidebar';
import AssistanceCard from './components/SmartSuggestions/AssistanceCard';
import SmartSuggestions from './components/SmartSuggestions/SmartSuggestions';
import useStepper from './hooks/useStepper';
import {
  HEADER_ANIMATION_DELAY_MS,
  INTRO_ANIMATION_DURATION_MS,
  INTRO_STEP_DEFAULT_DEFINITION,
  NARROW_SCREEN_BREAKPOINT,
  REVIEW_STEP_DEFAULT_DEFINITION
} from './lib/constants';

import '../OnboardingWizard/components/IntroStep/IntroStep.scss';

const EMPTY_SET = new Set();

const Stepper = ({
  wizardState = {},
  practice = {},
  isAdmin = false,
  wizardKind,
  hiddenSubstepCodes = EMPTY_SET,
  onShowTour
} = {}) => {
  const timeoutRef = useRef(null);
  const userState = useRecoilValue(state.userState);
  const currentUserId = userState?.user_id ?? userState?.id;
  const [showTasksIntro, setShowTasksIntro] = useState(userState?.authenticated);
  const [headerButtonProps, setHeaderButtonProps] = useState(null);
  const [headerInfoTooltip, setHeaderInfoTooltip] = useState(null);
  const [isEnteringFromIntro, setIsEnteringFromIntro] = useState(false);
  const [shouldAnimateHeader, setShouldAnimateHeader] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );
  const mainContentRef = useRef(null);
  const isScrolledRef = useRef(false);
  const hasBeenTriggeredRef = useRef(false); // Lock to prevent reverting
  const { instance = {}, steps: initialSteps = [], phases = null } = wizardState;
  const navigate = useNavigate();
  const location = useLocation();

  const isNarrowScreen = windowWidth <= NARROW_SCREEN_BREAKPOINT;
  const handleExitIntro = () => {
    setShowTasksIntro(false);
    onShowTour?.();
  };
  // Track window width for responsive behavior (debounced)
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setWindowWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close sidebar when switching to wide screen
  useEffect(() => {
    if (!isNarrowScreen) {
      setSidebarOpen(false);
    }
  }, [isNarrowScreen]);

  const closeSidebar = useCallback(() => {
    if (isNarrowScreen) {
      setSidebarOpen(false);
    }
  }, [isNarrowScreen]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const baseUrl = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const onboardingIndex = pathParts.indexOf('onboarding');
    if (onboardingIndex > -1) {
      return pathParts.slice(0, onboardingIndex + 1).join('/');
    }
    return location.pathname;
  }, [location.pathname]);

  const stepsWithIntroAndReview = useMemo(() => {
    return [INTRO_STEP_DEFAULT_DEFINITION, ...initialSteps, REVIEW_STEP_DEFAULT_DEFINITION];
  }, [initialSteps]);

  const {
    stepsState,
    activeStep,
    activeSubstep,
    activateStep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    navigateTo,
    goNext,
    goPrev
  } = useStepper(stepsWithIntroAndReview, {
    syncWithUrl: true,
    baseUrl
  });

  const activateStepWithHiddenCheck = useCallback(
    (stepCode) => {
      if (hiddenSubstepCodes.size === 0) {
        activateStep(stepCode);
        return;
      }
      const step = stepsState.find((s) => s.code === stepCode);
      const firstVisible = step?.substeps?.find((sub) => !hiddenSubstepCodes.has(sub.code));
      if (firstVisible && hiddenSubstepCodes.has(step?.substeps?.[0]?.code)) {
        navigateToSubstep(firstVisible.code);
      } else {
        activateStep(stepCode);
      }
    },
    [activateStep, navigateToSubstep, stepsState, hiddenSubstepCodes]
  );

  const [footerConfig, setFooterConfig] = useState({
    message: 'Changes are auto-saved.',
    goBackHandler: () => {},
    actions: []
  });

  const handleIntroNext = useCallback(() => {
    setIsEnteringFromIntro(true);
    goNext();

    timeoutRef.current = setTimeout(() => {
      setIsEnteringFromIntro(false);
    }, INTRO_ANIMATION_DURATION_MS);
  }, [goNext]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const { data: taskDelegationData } = useGetWizardDelegatedTasks({
    params: {
      wizard_instance_id: instance?.id,
      get_all_delegations: true,
      practiceId: instance?.instance_id,
      limit: 100
    },
    options: { enabled: Boolean(instance?.id) }
  });

  useEffect(() => {
    setFooterConfig((prev) => ({ ...prev, goBackHandler: goPrev }));
  }, [goPrev]);

  // Reset info tooltip when navigating to a different substep
  useEffect(() => {
    setHeaderInfoTooltip(null);
  }, [activeSubstep?.code]);

  // Scroll-based header compact trigger
  useEffect(() => {
    const mainElement = mainContentRef.current;
    if (!mainElement) return;

    // Reset scroll and state when substep changes
    mainElement.scrollTop = 0;
    setShouldAnimateHeader(false);
    isScrolledRef.current = false;
    hasBeenTriggeredRef.current = false; // Reset lock on substep change

    // Use hysteresis to prevent flickering
    const scrollDownThreshold = 1; // Trigger on any scroll down
    const scrollUpThreshold = 0; // Only revert when exactly at top
    let timeoutId = null;

    const checkIfScrollable = () => {
      return mainElement.scrollHeight > mainElement.clientHeight;
    };

    const revertHeader = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      hasBeenTriggeredRef.current = false;
      isScrolledRef.current = false;
      setShouldAnimateHeader(false);
    };

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;

      // If content is scrollable and we're at the top, revert header
      if (hasBeenTriggeredRef.current && checkIfScrollable() && scrollTop === 0) {
        // Clear any pending timeout before reverting
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        revertHeader();
        return;
      }

      // Once triggered, lock the compact state - only allow reverting on explicit user action
      let shouldBeScrolled;
      if (hasBeenTriggeredRef.current) {
        // Already triggered - stay compact (locked state)
        shouldBeScrolled = true;
      } else if (isScrolledRef.current) {
        // Already scrolled - only revert if we're at the very top
        shouldBeScrolled = scrollTop > scrollUpThreshold;
      } else {
        // Not scrolled - use threshold to enter
        shouldBeScrolled = scrollTop > scrollDownThreshold;
        if (shouldBeScrolled) {
          // Lock the state once triggered
          hasBeenTriggeredRef.current = true;
        }
      }

      // Only update if there's an actual state change (use ref to avoid dependency)
      if (shouldBeScrolled !== isScrolledRef.current) {
        isScrolledRef.current = shouldBeScrolled;

        // Only set timeout if one isn't already pending
        if (!timeoutId) {
          // Delay header animation to ensure padding is applied first
          timeoutId = setTimeout(() => {
            setShouldAnimateHeader(shouldBeScrolled);
            timeoutId = null;
          }, HEADER_ANIMATION_DELAY_MS);
        }
      }
    };

    // Handle wheel events when content is not scrollable (for scroll attempts)
    const handleWheel = (e) => {
      if (hasBeenTriggeredRef.current && !checkIfScrollable()) {
        // Content is not scrollable, check if user is trying to scroll up
        if (e.deltaY < 0) {
          // User is trying to scroll up
          revertHeader();
        }
      }
    };

    mainElement.addEventListener('scroll', handleScroll, { passive: true });
    mainElement.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      mainElement.removeEventListener('scroll', handleScroll);
      mainElement.removeEventListener('wheel', handleWheel);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeSubstep?.code]);

  const updateConfigs = useCallback((values) => setFooterConfig(values), []);

  const addFooterActions = useCallback(
    (values) => setFooterConfig((prev) => ({ ...prev, actions: values })),
    []
  );

  const ActiveComponent =
    componentSteps[activeSubstep?.code]?.component || StepPlaceholder || Fragment;
  const headerOptions = componentSteps[activeSubstep?.code]?.headerOptions || {};
  const smartSuggestions = componentSteps[activeSubstep?.code]?.smart_suggestions || [];

  const orchestratorData = useMemo(
    () => ({
      instanceId: instance?.id,
      wizardKind,
      practiceId: instance?.instance_id,
      stepCode: activeStep?.code,
      substepCode: activeSubstep?.code,
      stepId: activeStep?.step_id ?? activeStep?.id,
      substepId: activeSubstep?.substep_id ?? activeSubstep?.id,
      stepName: activeStep?.name,
      substepName: activeSubstep?.name,
      substepStatus: activeSubstep?.status,
      handlerStatus: activeSubstep?.status
    }),
    [instance?.id, instance?.instance_id, wizardKind, activeStep, activeSubstep]
  );

  const progressValue = useMemo(() => {
    const n = Number(instance?.progress);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
  }, [instance?.progress]);

  //This will filter only the tasks assigned to the current user
  const assignedTasks = useMemo(() => {
    const steps = taskDelegationData?.data?.delegations?.steps ?? [];
    const substeps = taskDelegationData?.data?.delegations?.substeps ?? [];

    const filterAndMap = (items) =>
      items
        .filter(
          (s) =>
            Array.isArray(s.assignments) && s.assignments.some((a) => a.user_id === currentUserId)
        )
        .map((s) => ({
          ...s,
          assignments: s.assignments.filter((a) => a.user_id === currentUserId)
        }));

    return [...filterAndMap(steps), ...filterAndMap(substeps)];
  }, [
    taskDelegationData?.data?.delegations?.steps,
    taskDelegationData?.data?.delegations?.substeps,
    currentUserId
  ]);

  const isIntroStep = activeSubstep?.code === INTRO_STEP_DEFAULT_DEFINITION.substeps[0].code;

  // Render full-screen IntroStep when on intro
  if (isIntroStep) {
    return (
      <div className="flex h-screen w-screen">
        <IntroStep onNext={handleIntroNext} />
      </div>
    );
  }

  return (
    <>
      {wizardState?.instance?.assigned_to !== currentUserId && !isAdmin && showTasksIntro && (
        <DelegationTasksIntro
          title="Myriad Health"
          description="The most comprehensive practice management system ever created."
          onEndIntro={handleExitIntro}
          onSkip={() => navigate('/portal')}
          navigateToTask={(code) => {
            navigateTo(code);
            handleExitIntro();
          }}
          tasks={assignedTasks}
        />
      )}

      <div
        className={cn(
          'h-screen bg-gradient-to-b from-primary-50 to-primary-200',
          isNarrowScreen ? 'p-0' : 'px-8 py-6'
        )}>
        <div
          className={cn(
            'relative flex h-full justify-center',
            isNarrowScreen ? 'gap-0' : 'gap-10'
          )}>
          {/* Sidebar for wide screens only - narrow screen sidebar is at top level */}
          {!isNarrowScreen && (
            <aside className="flex h-full w-80 flex-none flex-col gap-4 backdrop-blur-sm">
              <div
                className={cn(
                  'stepper-sidebar flex min-h-0 flex-1 flex-col',
                  isEnteringFromIntro && 'entering'
                )}>
                <StepSidebar
                  stepsState={stepsState}
                  phases={phases}
                  hiddenSubstepCodes={hiddenSubstepCodes}
                  onExpandStep={expandStep}
                  onCollapseStep={collapseStep}
                  onActivateStep={activateStepWithHiddenCheck}
                  onNavigateSubstep={navigateToSubstep}
                />
              </div>
              <div
                className={cn(
                  'stepper-smart-suggestions flex-shrink-0',
                  isEnteringFromIntro && 'entering'
                )}>
                <SmartSuggestions smart_suggestions={smartSuggestions} />
              </div>
              <div
                className={cn(
                  'stepper-assistance-card flex-shrink-0',
                  isEnteringFromIntro && 'entering'
                )}>
                <AssistanceCard isAdmin={isAdmin} practiceId={practice?.id} />
              </div>
            </aside>
          )}

          <div
            className={cn(
              'stepper-main-content relative w-full min-w-0 max-w-[1200px] flex-1 justify-center overflow-hidden bg-white',
              isNarrowScreen ? 'rounded-none' : 'rounded-xl',
              isEnteringFromIntro && 'entering'
            )}
            onClick={closeSidebar}>
            <div className="stepper-content flex h-full max-h-screen w-full flex-1 flex-col items-center rounded-xl bg-white">
              <div
                className={cn(
                  'stepper-main-content w-full transition-all duration-300 ease-in-out',
                  isEnteringFromIntro && 'entering',
                  shouldAnimateHeader
                    ? 'max-w-full'
                    : 'max-w-[856px] xl:max-w-[968px] xxl:max-w-[968px]'
                )}>
                <Header
                  headerButtonProps={headerButtonProps}
                  headerOptions={headerOptions}
                  headerInfoTooltip={headerInfoTooltip}
                  isAdmin={isAdmin}
                  progress={progressValue}
                  isCompact={shouldAnimateHeader}
                  isNarrowScreen={isNarrowScreen}
                  onToggleSidebar={toggleSidebar}
                />
              </div>

              <main
                ref={mainContentRef}
                className={cn(
                  'stepper-main-content relative min-h-0 w-full flex-1 overflow-y-auto bg-white',
                  isEnteringFromIntro && 'entering'
                )}>
                <div className="mx-auto flex h-full min-h-0 w-full max-w-[856px] flex-col xl:max-w-[968px] xxl:max-w-[968px]">
                  <ActiveComponent
                    setHeaderButtonProps={setHeaderButtonProps}
                    setHeaderInfoTooltip={setHeaderInfoTooltip}
                    isAdmin={isAdmin}
                    substepCode={activeSubstep?.code}
                    wizardState={{ instance, steps: stepsState }}
                    setFooterConfig={setFooterConfig}
                    updateConfigs={updateConfigs}
                    addFooterActions={addFooterActions}
                    onNext={goNext}
                    onPrev={goPrev}
                    defaultActions={componentSteps[activeSubstep?.code]?.stepActions}
                    message={componentSteps[activeSubstep?.code]?.message}
                    orchestratorData={orchestratorData}
                    navigateToSubstep={navigateToSubstep}
                  />
                </div>
              </main>

              {footerConfig?.aboveFooterContent && (
                <div className="w-full border-x-0 border-b-0 border-t border-solid border-neutral-200 bg-white">
                  <div className="w-full px-4">{footerConfig.aboveFooterContent}</div>
                </div>
              )}

              <div className={cn('stepper-footer w-full', isEnteringFromIntro && 'entering')}>
                <Footer
                  isAdmin={isAdmin}
                  activeSubstep={activeSubstep}
                  orchestratorData={orchestratorData}
                  delegations={taskDelegationData?.data?.delegations}
                  footerConfig={footerConfig}
                  wizardState={wizardState}
                  navigateToSubstep={navigateToSubstep}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for narrow screens when sidebar is open - rendered last to cover everything */}
      {isNarrowScreen && sidebarOpen && (
        <div
          onClick={closeSidebar}
          className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-[2px] transition-opacity duration-300"
        />
      )}

      {/* Sidebar for narrow screens - rendered last to appear above overlay */}
      {isNarrowScreen && (
        <aside
          className={cn(
            'fixed left-0 top-0 z-[110] flex h-full w-80 flex-col gap-4 bg-transparent p-4 transition-transform duration-300 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}>
          <div
            className={cn(
              'stepper-sidebar flex min-h-0 flex-1 flex-col',
              isEnteringFromIntro && 'entering'
            )}>
            <StepSidebar
              stepsState={stepsState}
              phases={phases}
              hiddenSubstepCodes={hiddenSubstepCodes}
              onExpandStep={(code) => {
                expandStep(code);
              }}
              onActivateStep={(code) => {
                activateStepWithHiddenCheck(code);
                closeSidebar();
              }}
              onNavigateSubstep={(code) => {
                navigateToSubstep(code);
                closeSidebar();
              }}
            />
          </div>
          <div
            className={cn(
              'stepper-smart-suggestions flex-shrink-0',
              isEnteringFromIntro && 'entering'
            )}>
            <SmartSuggestions smart_suggestions={smartSuggestions} />
          </div>
          <div
            className={cn(
              'stepper-assistance-card flex-shrink-0',
              isEnteringFromIntro && 'entering'
            )}>
            <AssistanceCard isAdmin={isAdmin} practiceId={practice?.id} />
          </div>
        </aside>
      )}
    </>
  );
};

export default Stepper;
